import {
  AlignmentType,
  Document,
  ImageRun,
  Packer,
  Paragraph,
  TextRun,
} from "docx";
import mammoth from "mammoth/mammoth.browser";

export async function readDocxAsHtml(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.convertToHtml({ arrayBuffer });
  return result.value || "<p></p>";
}

export async function exportEditorHtmlToDocx(html: string): Promise<Blob> {
  const container = document.createElement("div");
  container.innerHTML = html;
  const children = Array.from(container.childNodes);
  const paragraphs: Paragraph[] = [];

  for (const node of children) {
    paragraphs.push(...(await nodeToParagraphs(node)));
  }

  if (paragraphs.length === 0) {
    paragraphs.push(new Paragraph(""));
  }

  const doc = new Document({
    sections: [
      {
        properties: {},
        children: paragraphs,
      },
    ],
  });

  return Packer.toBlob(doc);
}

async function nodeToParagraphs(node: ChildNode): Promise<Paragraph[]> {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent?.trim();
    return text ? [new Paragraph({ children: [new TextRun(text)] })] : [];
  }

  if (!(node instanceof HTMLElement)) {
    return [];
  }

  if (node instanceof HTMLImageElement && node.src.startsWith("data:image/")) {
    return [await imageParagraph(node)];
  }

  const images = Array.from(node.querySelectorAll<HTMLImageElement>("img[data-signature]"));
  if (images.length > 0) {
    const text = node.textContent?.trim();
    const result: Paragraph[] = [];
    if (text) result.push(new Paragraph({ children: [new TextRun(text)] }));
    for (const image of images) result.push(await imageParagraph(image));
    return result;
  }

  const text = normalizeText(node.textContent ?? "");
  if (!text) return [];
  return text.split("\n").filter(Boolean).map((line) => new Paragraph({ children: [new TextRun(line)] }));
}

async function imageParagraph(image: HTMLImageElement): Promise<Paragraph> {
  const response = await fetch(image.src);
  const data = new Uint8Array(await response.arrayBuffer());
  return new Paragraph({
    alignment: AlignmentType.LEFT,
    children: [
      new ImageRun({
        data,
        transformation: {
          width: Math.min(image.naturalWidth || 320, 320),
          height: Math.min(image.naturalHeight || 120, 120),
        },
        type: "png",
      } as never),
    ],
  });
}

function normalizeText(text: string): string {
  return text.replace(/\u00a0/g, " ").replace(/[ \t]+\n/g, "\n").trim();
}
